<template>
  <div>

    <div class="tariff-box">
      <div class="tariff--volumes">
        <VolumesAboOptionVersionB
          ref="volumesAboOptionComp"
          :availableAboOptions="availableAboOptions"
          :targetTariff="targetTariff"
          :volumePromotions="volumePromotions"
          :showPriceStarts="showPriceStarts"
          :targetPromotions="targetPromotions"
          :promoPrice="promoPrice"
          :excludePromotionIds="excludePromotionIds"
          :normalPrice="normalPrice"
          :process="process"
          :defaultTxtProductHighlights="getSlotText('txtProductHighlights')"
          :defaultInfoModalHighlights="getSlotText('txtMoreInfoModal')"
          :lblMoreInfoButton="getSlotText('lblMoreInfoButton')"
          :ariaLabelMoreInfo="getSlotText('ariaLabelMoreInfo')"
          :lblOrderButton="getSlotText('lblOrderButton')"
          :txtLegalTerms="getSlotText('txtLegalTerms')"
          :txtInfoIconAlt="getSlotText('txtInfoIconAlt')"
          :lblAriaLabelCTA="getSlotText('lblAriaLabelCTA')"
          :pricePromoColor="getSlotText('pricePromoColor')"
          :priceColor="getSlotText('priceColor')"
          :txtPriceCurrency="getSlotText('txtPriceCurrency')"
        >
          <template v-for="slot in possibleChildSlotsFor('AboOptionWrapper')" v-slot:[`${slot.child}`]>
            <slot :name="`${slot.parent}`"></slot>
          </template>
          <div class="volumes-abo-option">
            <fieldset class="form-group">^
              <div role="radiogroup" tabindex="-1" class="btn-group-toggle btn-group bv-no-focus-ring"></div>
            </fieldset>
          </div>
        </VolumesAboOptionVersionB>
      </div>
      <div class="tariff--boxes">
        <template v-if="infoBoxesForOption && infoBoxesForOption.length">
          <div v-for="(infoBox, index) in infoBoxesForOption" :key="index">
            <InfoBox v-bind="infoBox" class="card"></InfoBox>
          </div>
        </template>
        <template v-if="infoBoxes && infoBoxes.length">
          <div v-for="(infoBox, index) in infoBoxes" :key="index">
            <InfoBox v-bind="infoBox" class="card"></InfoBox>
          </div>
        </template>
        <template v-if="voucherBoxes && voucherBoxes.length">
          <div v-for="(voucherBox, index) in voucherBoxes" :key="index">
            <VoucherBox v-bind="voucherBox" class="card" :process="process"></VoucherBox>
          </div>
        </template>
      </div>
    </div>
    <div class="tariff--bottom">
      <div class="bottom--pib">
        <a class="button text" target="_blank" href="/downloads/pib" rel="noopener"><slot name="lblPibLink"></slot></a>
      </div>
    </div>
  </div>
</template>

<script>
import VolumesAboOptionVersionB from '@/components/checkout/tariff/generic/VolumesAboOptionVersionB.vue'
import VoucherBox from '@/components/checkout/tariff/generic/VoucherBox.vue'
import InfoBox from '@/components/checkout/tariff/generic/InfoBox.vue'
import price from '@/mixins/formatters/price'
import { mapGetters } from 'vuex'
import trackingHandler from '@/mixins/trackingHandler'
import useFeature from '@/mixins/useFeature'

export default {
  name: 'TariffVersionB',
  data () {
    return {
      showMoreInfoModal: false,
      ariaPriceText: '',
      ariaLivePriceText: 'off'
    }
  },
  mixins: [
    price,
    trackingHandler,
    useFeature('birthdayPromo')
  ],
  components: {
    VoucherBox,
    InfoBox,
    VolumesAboOptionVersionB
  },
  computed: {
    ...mapGetters('promotion', ['presalesInclusivePromotion']),
    mnpProvider: function () {
      return this.$store.getters['checkoutv2/' + this.process + '/_cart/mnpProvider']
    },
    selectedAboOptionId: function () {
      return this.$store.getters['checkoutv2/' + this.process + '/_cart/selectedTariffOptionIds']?.length > 0 ? this.$store.getters['checkoutv2/' + this.process + '/_cart/selectedTariffOptionIds'][0] : []
    },
    selectedAboOption: function () {
      const aboOption = this.availableAboOptions.find(option => option.optionId === this.selectedAboOptionId)
      return aboOption || this.availableAboOptions.find(option => option.optionId === 'none')
    },
    availableAboOptions: function () {
      try {
        return this.getSlotText('availableAboOptionsJSON') !== ''
          ? JSON.parse(this.getSlotText('availableAboOptionsJSON'))
          : []
      } catch (e) {
        console.error('Error parsing JSON data:', e)
        return []
      }
    },
    infoBoxesForOption: function () {
      return this.selectedAboOption?.infoBoxes || []
    },
    infoBoxes: function () {
      try {
        return this.getSlotText('infoBoxesJSON') !== ''
          ? JSON.parse(this.getSlotText('infoBoxesJSON'))
          : []
      } catch (e) {
        console.error('Error parsing JSON data:', e)
        return []
      }
    },
    voucherBoxes: function () {
      try {
        return this.getSlotText('voucherBoxesJSON') !== ''
          ? JSON.parse(this.getSlotText('voucherBoxesJSON'))
          : []
      } catch (e) {
        console.error('Error parsing JSON data:', e)
        return []
      }
    },
    filteredPromotions: function () {
      return this.targetPromotions.filter(promo => {
        // promotions that need e.g. voucher code are not displayed in tariff
        if (promo.requiredParameters !== null) return false

        // Tariff Filter -> only promotions that accept the current targetTariff
        if (this.targetTariff && this.targetTariff.tariffId !== null) {
          const tariffIds = promo.criteria?.tariffIds || []
          if (tariffIds.length > 0 && tariffIds.includes(this.targetTariff.tariffId) === false) return false
        }

        // Provider Filter -> only promotions that dont need a certain provider
        const criteriaMnpProvider = promo.criteria?.mnpProvider || []
        return criteriaMnpProvider.length === 0
      })
    },
    creditPromotions () { // only price promotions (permanent and temporary)
      return this.filteredPromotions.filter(promo => promo.incentives?.find(incentive => incentive.type === 'credit'))
    },
    volumePromotions () { // only permanent volume promotions
      return this.filteredPromotions.filter(promo => promo.incentives?.find(incentive => incentive.type === 'volume' && (promo.duration === null)))
    },
    hasPricePromotion () {
      return this.creditPromotions.length > 0
    },
    hasStrikePrice () { // strike price uses promo filter, targetPromos does not
      return this.promoPrice.promoAmount !== this.fakeNormalPriceForBirthdayPromo // this.normalPrice.amount
    },
    fakeNormalPriceForBirthdayPromo () {
      if (this.birthdayPromo && this.selectedAboOptionId === '3036138') {
        return this.normalPrice.amount + 500
      }
      return this.normalPrice.amount
    }
  },
  methods: {
    slotDataForStairway (stairway) {
      const price = { ...stairway }
      const prefix = this.showPriceStarts ? 'ab ' : ''
      price.promoAmount = prefix + this.priceWithoutEuro(stairway.promoAmount)
      return {
        promoPrice: price
      }
    }
  },
  watch: {
    promoPrice: {
      handler () {
        this.ariaLivePriceText = 'polite'
      },
      deep: true
    }
  },
  props: {
    disableProviderSelect: {
      type: Boolean,
      default: false
    },
    quickSelectionBrand: {
      type: String,
      default: ''
    },
    showPriceStarts: {
      type: Boolean,
      default: false
    },
    normalPrice: {
      type: Object,
      default: null
    },
    promoPrice: {
      type: Object,
      default: null
    },
    additionalPriceStairways: {
      type: Array,
      default: () => []
    },
    targetTariff: {
      type: Object,
      default: null
    },
    targetPromotions: {
      type: Array,
      default: () => []
    },
    excludePromotionIds: {
      type: Array,
      default: () => []
    },
    process: {
      type: String,
      default: 'ordering'
    }
  }
}
</script>
